/**
 * Domain-suggestion payload attached to an `email_domain` validation
 * failure, per the shared contract [GRO-6465]. `suggestion` is the domain
 * alone (e.g. `gmail.com`); `suggested_email` is the full address with the
 * local part preserved verbatim, including case and any `+tag`.
 */
export interface EmailDomainSuggestion {
  suggestion: string;
  suggested_email: string;
}

/**
 * Safely extracts a validated `email_domain` suggestion from an arbitrary,
 * possibly-malformed payload — a REST error body (`{ email_domain: {...} }`)
 * or a GraphQL error's `extensions` (`{ email_domain: {...} }`).
 *
 * Per contract, the key is present only when a suggestion was found and is
 * never `null`/`{}`/partially-populated. Any other shape — missing, wrong
 * types, empty strings — is treated as "no suggestion" rather than an error.
 * This must never throw: a malformed suggestion payload must never block a
 * guest from seeing their original error or submitting again [GRO-6429].
 */
export function extractEmailDomainSuggestion(payload: unknown): EmailDomainSuggestion | null {
  try {
    if (typeof payload !== 'object' || payload === null) return null;

    const emailDomain = (payload as Record<string, unknown>).email_domain;
    if (typeof emailDomain !== 'object' || emailDomain === null) return null;

    const { suggestion, suggested_email } = emailDomain as Record<string, unknown>;
    if (typeof suggestion !== 'string' || suggestion.length === 0) return null;
    if (typeof suggested_email !== 'string' || suggested_email.length === 0) return null;

    return { suggestion, suggested_email };
  } catch {
    return null;
  }
}
